import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import { DatePeriod, PeriodPickerView } from './m-compare-period-picker.types';
import {
  getMonthLabels,
  getQuarterRangeLabel,
  isPeriodSelectable,
  monthPeriod,
  parseIsoDate,
  PeriodBounds,
  quarterPeriod,
  yearPeriod,
  yearRangeLabel,
  yearsInBounds
} from './m-compare-period-picker.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-period-picker',
  templateUrl: './m-period-picker.component.html',
  host: {
    class: 'flex h-full flex-col'
  },
  imports: []
})
export class MPeriodPickerComponent {
  readonly view = input.required<PeriodPickerView>();
  readonly value = input<DatePeriod | null>(null);
  /** Inclusive lower bound (YYYY-MM-DD), same convention as m-calendar / m-fiscal-calendar. */
  readonly minDate = input<string | null>(null);
  /** Inclusive upper bound (YYYY-MM-DD). */
  readonly maxDate = input<string | null>(null);
  /**
   * When set (including `null` / `[]`), only these periods are selectable.
   * Omit to keep bounds-only behaviour.
   */
  readonly allowedPeriods = input<DatePeriod[] | null | undefined>(undefined);
  readonly footerLabel = input<string | null>(null);

  readonly periodChange = output<DatePeriod>();

  readonly monthLabels = getMonthLabels();
  readonly quarters = [0, 1, 2, 3] as const;

  readonly displayYear = signal(new Date().getFullYear());

  private readonly syncDisplayYear = effect(() => {
    const selected = this.value();
    if (selected?.startDate) {
      this.displayYear.set(parseIsoDate(selected.startDate).getFullYear());
    }
  });

  readonly bounds = computed(
    (): PeriodBounds => ({
      minDate: this.minDate(),
      maxDate: this.maxDate()
    })
  );

  readonly years = computed(() => yearsInBounds(this.bounds()));

  readonly yearHeaderLabel = computed(() => {
    if (this.view() === 'year') {
      return yearRangeLabel(this.bounds());
    }
    return String(this.displayYear());
  });

  readonly canNavigatePrevYear = computed(() => {
    if (this.view() === 'year') return false;
    const years = this.years();
    if (years.length === 0) return true;
    return this.displayYear() > Math.min(...years);
  });

  readonly canNavigateNextYear = computed(() => {
    if (this.view() === 'year') return false;
    const years = this.years();
    if (years.length === 0) return true;
    return this.displayYear() < Math.max(...years);
  });

  onPrevYear(): void {
    if (!this.canNavigatePrevYear()) return;
    this.displayYear.update((year) => year - 1);
  }

  onNextYear(): void {
    if (!this.canNavigateNextYear()) return;
    this.displayYear.update((year) => year + 1);
  }

  isMonthSelected(monthIndex: number): boolean {
    const selected = this.value();
    if (!selected) return false;
    const date = parseIsoDate(selected.startDate);
    return (
      date.getFullYear() === this.displayYear() &&
      date.getMonth() === monthIndex
    );
  }

  isMonthAvailable(monthIndex: number): boolean {
    return isPeriodSelectable(
      monthPeriod(this.displayYear(), monthIndex),
      this.bounds(),
      this.allowedPeriods()
    );
  }

  onMonthClick(monthIndex: number): void {
    if (!this.isMonthAvailable(monthIndex)) return;
    this.periodChange.emit(monthPeriod(this.displayYear(), monthIndex));
  }

  isQuarterSelected(quarterIndex: number): boolean {
    const selected = this.value();
    if (!selected) return false;
    const date = parseIsoDate(selected.startDate);
    return (
      date.getFullYear() === this.displayYear() &&
      Math.floor(date.getMonth() / 3) === quarterIndex
    );
  }

  isQuarterAvailable(quarterIndex: number): boolean {
    return isPeriodSelectable(
      quarterPeriod(this.displayYear(), quarterIndex),
      this.bounds(),
      this.allowedPeriods()
    );
  }

  onQuarterClick(quarterIndex: number): void {
    if (!this.isQuarterAvailable(quarterIndex)) return;
    this.periodChange.emit(quarterPeriod(this.displayYear(), quarterIndex));
  }

  quarterRangeLabel(quarterIndex: number): string {
    return getQuarterRangeLabel(quarterIndex);
  }

  isYearSelected(year: number): boolean {
    const selected = this.value();
    if (!selected) return false;
    return parseIsoDate(selected.startDate).getFullYear() === year;
  }

  isYearAvailable(year: number): boolean {
    return isPeriodSelectable(
      yearPeriod(year),
      this.bounds(),
      this.allowedPeriods()
    );
  }

  onYearClick(year: number): void {
    if (!this.isYearAvailable(year)) return;
    this.periodChange.emit(yearPeriod(year));
  }
}
